import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { z, ZodError } from 'zod';
import { ErrorCode } from '@web/common';
import { RequestError } from './RequestError';
import { RequestErrorType } from './RequestErrorType';
import { ServerError } from './ServerError';

class PublicAPI {
  private readonly client: AxiosInstance;

  constructor(requestTimeout = 10000) {
    this.client = axios.create({
      timeout: requestTimeout,
      headers: { 'Content-Type': 'application/json' },
      baseURL: process.env.NX_PUBLIC_API_URL,
    });
  }

  // TODO CBYN-187 fix
  public async get<ReturnType>(
    resourceUrl: string,
    schema?: z.Schema<ReturnType>,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<ReturnType> {
    const result = await this.client.get(resourceUrl, {
      ...axiosConfig,
    });

    if (schema) return schema.parse(result.data);

    return result.data as ReturnType;
  }

  //TODO CBYN-141 fix profile picture
  public async getImage(
    resourceUrl: string,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<ArrayBuffer> {
    const response = await this.client
      .get(resourceUrl, {
        ...axiosConfig,
      })
      .catch(this.handleAPIError);
    return response.data;
  }

  public async post<Type, ReturnType>(
    resourceUrl: string,
    data: Type,
    schema: z.Schema<ReturnType>,
    config?: AxiosRequestConfig,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.post<ReturnType>(resourceUrl, data, {
          ...config,
        }),
      schema,
    );
  }

  public async patch<Type, ReturnType>(
    resourceUrl: string,
    data: Type,
    schema: z.Schema<ReturnType>,
    config?: AxiosRequestConfig,
  ): Promise<ReturnType> {
    return this.request(
      () => this.client.patch<ReturnType>(resourceUrl, data, { ...config }),
      schema,
    );
  }

  public async put<Type, ReturnType>(
    resourceUrl: string,
    data: Type,
    schema: z.Schema<ReturnType>,
    config?: AxiosRequestConfig,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.put<ReturnType>(resourceUrl, data, {
          ...config,
        }),
      schema,
    );
  }

  public async noBodyPut<ReturnType>(
    resourceUrl: string,
    schema: z.Schema<ReturnType>,
  ): Promise<ReturnType> {
    return this.request(() => this.client.put<ReturnType>(resourceUrl), schema);
  }

  public async delete<ReturnType>(
    resourceUrl: string,
    schema: z.Schema<ReturnType>,
    config?: AxiosRequestConfig,
  ): Promise<ReturnType> {
    return this.request(
      () =>
        this.client.delete<ReturnType>(resourceUrl, {
          ...config,
        }),
      schema,
    );
  }

  private async request<RequestData, ResponseData>(
    block: () => Promise<AxiosResponse<unknown, RequestData>>,
    schema: z.Schema<ResponseData>,
  ): Promise<ResponseData> {
    const response = await block().catch(this.handleAPIError);

    const parseResult = schema.safeParse(response.data);
    if (parseResult.success) {
      return parseResult.data;
    }

    throw this.handleParseError(parseResult.error, response.config);
  }

  private handleAPIError = (error: AxiosError): never => {
    const apiResponseData = error?.response?.data as
      | undefined
      | { errorMessage?: string; error?: string; errorCode?: string };

    const statusCode = error.response?.status;
    const serverErrorCode = apiResponseData?.errorCode as undefined | ErrorCode;

    console.error(
      JSON.stringify({
        statusCode: statusCode,
        error: error.message,
        body: apiResponseData,
      }),
    );

    if (serverErrorCode) {
      throw new ServerError(serverErrorCode);
    } else {
      throw new RequestError(RequestErrorType.Generic);
    }
  };

  private handleParseError<T>(error: ZodError<T>, config: AxiosRequestConfig) {
    // TODO CBYN-117
    /* Sentry.captureEvent({
      message: `Error: PublicAPI ${error} `,
      level: 'error',
      extra: { error },
    }); */

    throw new RequestError(RequestErrorType.ParseIssue, JSON.stringify(error));
  }
}

export const publicAPI = new PublicAPI();
